/**
 * DelegatePayments - Track and manage delegate payment status for events
 * Integrates with the organization finance system
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { formatCurrency as fmtCurrency } from '@/lib/currency';
import {
  DollarSign, TrendingUp, Users, CreditCard, CheckCircle2, Clock,
  AlertCircle, Loader2, RefreshCw, Mail, Receipt, Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DelegatePayments = ({ eventId }) => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedDelegate, setSelectedDelegate] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_method: 'cash',
    reference_number: '',
    send_receipt: true,
    receipt_method: 'email'
  });

  // Fetch event finance summary
  const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } = useQuery({
    queryKey: ['event-finance-summary', eventId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/events/${eventId}/finance/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch summary');
      return res.json();
    },
    enabled: !!eventId && !!token
  });

  // Fetch delegate payment status
  const { data: delegatesData, isLoading: delegatesLoading } = useQuery({
    queryKey: ['event-delegates-payments', eventId, filterStatus],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '200' });
      if (filterStatus !== 'all') params.append('status', filterStatus);
      const res = await fetch(`${API_URL}/api/events/${eventId}/delegates/payment-status?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch delegates');
      return res.json();
    },
    enabled: !!eventId && !!token
  });

  // Record payment mutation
  const recordPaymentMutation = useMutation({
    mutationFn: async ({ delegateId, data }) => {
      const res = await fetch(`${API_URL}/api/delegates/${delegateId}/payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          category: 'event_registration',
          date: new Date().toISOString().split('T')[0],
          ...data
        })
      });
      if (!res.ok) throw new Error('Failed to record payment');
      return res.json();
    },
    onSuccess: () => {
      toast.success('Payment recorded successfully!');
      queryClient.invalidateQueries(['event-delegates-payments', eventId]);
      queryClient.invalidateQueries(['event-finance-summary', eventId]);
      setShowPaymentDialog(false);
      setSelectedDelegate(null);
      setPaymentForm({
        amount: '',
        payment_method: 'cash',
        reference_number: '',
        send_receipt: true,
        receipt_method: 'email'
      });
    },
    onError: (err) => toast.error(err.message)
  });

  const handleRecordPayment = () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    recordPaymentMutation.mutate({
      delegateId: selectedDelegate.id,
      data: {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount)
      }
    });
  };

  const formatCurrency = (amount) => fmtCurrency(amount, 'USD');

  const delegates = delegatesData?.delegates || [];
  const filteredDelegates = delegates.filter(d => 
    !searchTerm || 
    d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3 mr-1" />Paid</Badge>;
      case 'partial':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="h-3 w-3 mr-1" />Partial</Badge>;
      default:
        return <Badge className="bg-red-100 text-red-800"><AlertCircle className="h-3 w-3 mr-1" />Pending</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Delegates</CardTitle>
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-500" />
                <span className="text-2xl font-bold">{summary?.delegates?.total || 0}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Paid</CardTitle>
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <span className="text-2xl font-bold text-green-600">{summary?.delegates?.paid || 0}</span>
                <span className="text-sm text-muted-foreground">({summary?.delegates?.payment_rate || 0}%)</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Expected Income</CardTitle>
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-blue-500" />
                <span className="text-2xl font-bold">{formatCurrency(summary?.summary?.expected_income)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Collected</CardTitle>
          </CardHeader>
          <CardContent>
            {summaryLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-500" />
                  <span className="text-2xl font-bold text-green-600">{formatCurrency(summary?.summary?.total_income)}</span>
                </div>
                <Progress 
                  value={summary?.summary?.collection_rate || 0} 
                  className="mt-2 h-2"
                />
                <span className="text-xs text-muted-foreground">{summary?.summary?.collection_rate || 0}% collected</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search delegates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="partial">Partial</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={() => {
          refetchSummary();
          queryClient.invalidateQueries(['event-delegates-payments', eventId]);
        }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Delegates Table */}
      <Card>
        <CardHeader>
          <CardTitle>Delegate Payment Status</CardTitle>
          <CardDescription>
            Track and record payments for event registrations
          </CardDescription>
        </CardHeader>
        <CardContent>
          {delegatesLoading ? (
            <Skeleton className="h-64" />
          ) : filteredDelegates.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No delegates found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Delegate</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead className="text-right">Expected</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDelegates.map((delegate) => (
                  <TableRow key={delegate.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{delegate.name}</div>
                        <div className="text-xs text-muted-foreground">{delegate.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{delegate.tier_name || 'Standard'}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(delegate.expected_amount)}
                    </TableCell>
                    <TableCell className="text-right text-green-600 font-medium">
                      {formatCurrency(delegate.total_paid)}
                    </TableCell>
                    <TableCell className="text-right text-red-600 font-medium">
                      {delegate.balance_due > 0 ? formatCurrency(delegate.balance_due) : '-'}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(delegate.payment_status)}
                    </TableCell>
                    <TableCell className="text-center">
                      {delegate.payment_status !== 'paid' && (
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedDelegate(delegate);
                            setPaymentForm({
                              ...paymentForm,
                              amount: delegate.balance_due.toString()
                            });
                            setShowPaymentDialog(true);
                          }}
                        >
                          <CreditCard className="h-4 w-4 mr-1" />
                          Pay
                        </Button>
                      )}
                      {delegate.payment_status === 'paid' && delegate.payment_count > 0 && (
                        <Badge variant="outline" className="text-green-600">
                          {delegate.payment_count} payment{delegate.payment_count > 1 ? 's' : ''}
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-green-500" />
              Record Payment
            </DialogTitle>
            <DialogDescription>
              {selectedDelegate && (
                <span>Recording payment for <strong>{selectedDelegate.name}</strong></span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {selectedDelegate && (
              <div className="bg-muted/50 p-3 rounded-lg space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Expected:</span>
                  <span className="font-medium">{formatCurrency(selectedDelegate.expected_amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Already Paid:</span>
                  <span className="font-medium text-green-600">{formatCurrency(selectedDelegate.total_paid)}</span>
                </div>
                <div className="flex justify-between border-t pt-1">
                  <span className="text-muted-foreground">Balance Due:</span>
                  <span className="font-bold text-red-600">{formatCurrency(selectedDelegate.balance_due)}</span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Amount *</Label>
              <Input
                type="number"
                step="0.01"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                placeholder="0.00"
              />
            </div>

            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select 
                value={paymentForm.payment_method} 
                onValueChange={(v) => setPaymentForm({ ...paymentForm, payment_method: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="mobile">Mobile Payment</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Reference Number</Label>
              <Input
                value={paymentForm.reference_number}
                onChange={(e) => setPaymentForm({ ...paymentForm, reference_number: e.target.value })}
                placeholder="Optional"
              />
            </div>

            <div className="flex items-center justify-between bg-muted/50 p-3 rounded-lg">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="send_receipt_payment"
                  checked={paymentForm.send_receipt}
                  onChange={(e) => setPaymentForm({ ...paymentForm, send_receipt: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <Label htmlFor="send_receipt_payment" className="text-sm cursor-pointer flex items-center gap-1">
                  <Mail className="h-4 w-4" />
                  Send Receipt
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRecordPayment} 
              disabled={recordPaymentMutation.isPending}
            >
              {recordPaymentMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DelegatePayments;
